import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CalendarClock, ExternalLink } from 'lucide-react';
import { billingApi, type LiveSubscriptionRow } from '../../services/billingApi';
import { api } from '../../services/apiService';
import { useDataVersion } from '../../services/dataChangeBus';
import type { DeviceModelRow, DeviceOrderRow } from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';
import {
  groupByStore, invoiceLocationId, isAddOn, orderDelta, orderSubscription, storeForecast, type StoreBill,
} from './locationBilling';

const money = (c?: number | null) =>
  typeof c === 'number' ? `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const monthLabel = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
const dayLabel = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** Test keys on dev and staging — a live-dashboard link would land on "no such object". */
const stripeBase = () => (/localhost|dev\.|staging\./.test(window.location.hostname)
  ? 'https://dashboard.stripe.com/test'
  : 'https://dashboard.stripe.com');

type Invoice = { id: string; number?: string; status: string; amount: number; amountPaid: number; periodStart: number; subscription?: string | null; locationId?: string | null };

/**
 * THE PAID STAGE, FOR THE STORE THIS ORDER SHIPS TO.
 *
 * It used to project the whole account's onboarding sale — every store's
 * software, summed — onto every order, so an add-on order for one store read
 * "$405.89 software" and showed none of the hardware it actually added. A
 * merchant pays per location, so this reads Stripe's live subscriptions,
 * groups them by store and opens on this order's: what the store paid before,
 * what this order adds, and the new monthly, then the next twelve months.
 *
 * `fallback` renders when Stripe cannot be read, so the stage never goes blank.
 */
const LocationPaidPanel: React.FC<{
  order: DeviceOrderRow;
  models: DeviceModelRow[];
  /** This order's store, by name, when the screen already resolved it. */
  orderLocationName?: string;
  fallback: React.ReactNode;
}> = ({ order, models, orderLocationName, fallback }) => {
  const dataVersion = useDataVersion();
  const [subs, setSubs] = useState<LiveSubscriptionRow[] | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const [methods, inv, reqs] = await Promise.all([
        billingApi.listPaymentMethods(order.tenantId).catch(() => null),
        api.superAdmin.getTenantInvoices(order.tenantId, 100).catch(() => null),
        api.locationBilling.listTenantRequests(order.tenantId).catch(() => null),
      ]);
      if (!live) return;
      setSubs(methods && (methods as any).readable ? ((methods as any).liveSubscriptions || []) : null);
      setInvoices(((inv as any)?.invoices || []) as Invoice[]);
      const rows: any[] = (reqs as any)?.requests ?? (reqs as any)?.items ?? (Array.isArray(reqs) ? reqs : []);
      const n: Record<string, string> = {};
      for (const r of rows) if (r?.locationId && r?.name) n[r.locationId] = String(r.name);
      if (orderLocationName) n[order.locationId] = orderLocationName;
      setNames(n);
      setLoading(false);
    })();
    return () => { live = false; };
  }, [order.tenantId, order.locationId, orderLocationName, dataVersion]);

  const stores = useMemo(() => groupByStore(subs || [], names, order.locationId), [subs, names, order.locationId]);
  const orderSub = useMemo(() => (subs ? orderSubscription(subs, order) : undefined), [subs, order]);

  if (loading) return <RubyLoader variant="inline" label="reading the subscriptions…" />;
  if (!subs || subs.length === 0) return <>{fallback}</>;

  const home = stores.find((s) => s.locationId === order.locationId);
  const key = picked ?? (home ? order.locationId : 'all');
  const store = key === 'all' ? null : stores.find((s) => (s.locationId || '') === key) || null;
  const accountCents = stores.reduce((n, s) => n + s.monthlyCents, 0);

  return (
    <div className="space-y-3.5 text-stone-900">
      {/* Which store. This order's is first and picked; the whole account is one tap away. */}
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Store">
        {stores.map((s) => {
          const k = s.locationId || '';
          const on = key === k;
          return (
            <button
              key={k || 'none'}
              type="button"
              aria-pressed={on}
              onClick={() => setPicked(k)}
              className={`min-h-[44px] rounded-lg border px-3 py-1.5 text-left leading-tight ${on
                ? 'border-stone-900 bg-white shadow-[inset_0_-2px_0_#DC143C]'
                : 'border-stone-200 bg-white hover:bg-stone-50'}`}
            >
              <span className="block text-[12.5px] font-semibold text-stone-900">{s.name}</span>
              <span className="block text-[11px] text-stone-500">
                {s.locationId === order.locationId ? 'this order · ' : ''}{money(s.monthlyCents)}/mo
              </span>
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={key === 'all'}
          onClick={() => setPicked('all')}
          className={`min-h-[44px] rounded-lg border px-3 py-1.5 text-left leading-tight ${key === 'all'
            ? 'border-stone-900 bg-white shadow-[inset_0_-2px_0_#DC143C]'
            : 'border-stone-200 bg-white hover:bg-stone-50'}`}
        >
          <span className="block text-[12.5px] font-semibold text-stone-900">Whole account</span>
          <span className="block text-[11px] text-stone-500">{money(accountCents)}/mo</span>
        </button>
      </div>

      {store
        ? <StoreView store={store} isHome={store.locationId === order.locationId} order={order} models={models} orderSub={orderSub} invoices={invoices} subs={subs} />
        : <AccountView stores={stores} accountCents={accountCents} onPick={(k) => setPicked(k)} />}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; sub?: string; strong?: boolean }> = ({ label, value, sub, strong }) => (
  <div className="flex flex-col gap-0.5 bg-stone-50/70 px-3.5 py-2.5">
    <span className="text-[11px] text-stone-500">{label}</span>
    <span className={`tabular-nums ${strong ? 'text-[19px] font-bold' : 'text-[17px] font-semibold'} text-stone-900`}>{value}</span>
    {sub && <span className="text-[11px] text-stone-400">{sub}</span>}
  </div>
);

const StoreView: React.FC<{
  store: StoreBill; isHome: boolean; order: DeviceOrderRow; models: DeviceModelRow[];
  orderSub?: LiveSubscriptionRow; invoices: Invoice[]; subs: LiveSubscriptionRow[];
}> = ({ store, isHome, order, models, orderSub, invoices, subs }) => {
  const mineSub = isHome ? orderSub : undefined;
  const delta = mineSub ? orderDelta(store, mineSub) : null;
  const rented = (order.items || []).filter((i) => i.saleMode && i.saleMode !== 'outright' && i.saleMode !== 'included');
  const term = Math.max(0, ...rented.map((i) => i.termMonths || 0)) || undefined;
  const termEnds = term ? new Date(new Date(order.createdAt).getFullYear(), new Date(order.createdAt).getMonth() + term, 1) : null;
  const rows = storeForecast(store, { orderSub: mineSub, orderStartedAt: order.createdAt, orderTermMonths: term });
  /*
    WHAT THIS ORDER CHARGED — the first invoice its own subscription raised.
    An add-on starts prorated to the store's billing day, so this is rarely a
    whole month and is quoted as charged, never as "the monthly".
  */
  const firstCharge = mineSub
    ? invoices.filter((i) => i.subscription === mineSub.subscriptionId).sort((a, b) => a.periodStart - b.periodStart)[0]
    : undefined;
  const storeInvoices = invoices.filter((i) => invoiceLocationId(i, subs) === store.locationId);
  const paidHere = storeInvoices.filter((i) => i.status === 'paid').reduce((n, i) => n + (i.amountPaid || 0), 0);
  const base = stripeBase();
  const nameOf = (modelId?: string, skuId?: string) => {
    const m = models.find((x) => x.modelId === modelId);
    return m ? `${m.manufacturer} ${m.modelName}` : (modelId || skuId || 'Device');
  };

  return (
    <div className="space-y-3.5">
      {delta ? (
        /* Before → this order → now: the one comparison a merchant asks about. */
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch overflow-hidden rounded-xl border border-stone-200">
          <Stat label="Before this order" value={`${money(delta.beforeCents)}/mo`} sub={`${store.name}`} />
          <span className="hidden sm:grid place-items-center bg-stone-50/70 px-1 text-stone-300"><ArrowRight size={14} /></span>
          <Stat label="This order adds" value={`+${money(delta.addsCents)}/mo`} sub={termEnds ? `until ${monthLabel(termEnds)}` : 'add-on subscription'} />
          <span className="hidden sm:grid place-items-center bg-stone-50/70 px-1 text-stone-300"><ArrowRight size={14} /></span>
          <Stat label="New monthly" value={`${money(delta.nowCents)}/mo`} sub={`${store.subs.length} subscription${store.subs.length === 1 ? '' : 's'}`} strong />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px overflow-hidden rounded-xl border border-stone-200 bg-stone-200">
          <Stat label="Store plan" value={`${money(store.storePlanCents)}/mo`} />
          <Stat label="Add-ons" value={`${money(store.addOnCents)}/mo`} />
          <Stat label="Per month" value={`${money(store.monthlyCents)}/mo`} strong />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-stone-600">
        {firstCharge && (
          <span>
            This order charged <b className="tabular-nums text-stone-900">{money(firstCharge.amountPaid || firstCharge.amount)}</b>
            {' '}on {dayLabel(firstCharge.periodStart)}{firstCharge.number ? ` · ${firstCharge.number}` : ''}
          </span>
        )}
        <span>Paid to date at this store <b className="tabular-nums text-stone-900">{money(paidHere)}</b></span>
      </div>

      {/* Every subscription at the store, each one a link into Stripe. */}
      <div className="flex flex-wrap gap-1.5">
        {store.subs.map((s) => (
          <a
            key={s.subscriptionId}
            href={`${base}/subscriptions/${s.subscriptionId}`}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border px-2.5 text-[11.5px] hover:bg-stone-50 ${s === mineSub ? 'border-rose-200 text-stone-900' : 'border-stone-200 text-stone-600'}`}
          >
            <span className="font-semibold">{s === mineSub ? 'This order' : isAddOn(s) ? 'Add-on' : 'Store plan'}</span>
            <span className="tabular-nums">{money(s.monthlyCents)}/mo</span>
            <span className="font-mono text-[10.5px] text-stone-400">{s.subscriptionId.slice(0, 12)}…</span>
            <ExternalLink size={11} className="text-stone-400" />
          </a>
        ))}
      </div>

      <div>
        <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-stone-400">
          <CalendarClock size={12} /> Next 12 months · {store.name}
        </div>
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="w-full min-w-[460px] text-[12.5px] tabular-nums">
            <thead>
              <tr className="bg-stone-50 text-left text-[10.5px] uppercase tracking-wider text-stone-400">
                <th className="px-3.5 py-2 font-bold">Month</th>
                <th className="px-3.5 py-2 font-bold">Store plan</th>
                <th className="px-3.5 py-2 font-bold">Add-ons</th>
                <th className="px-3.5 py-2 text-right font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.month.toISOString()} className="border-t border-stone-100">
                  <td className="px-3.5 py-2 text-stone-700">{monthLabel(r.month)}</td>
                  <td className="px-3.5 py-2 text-stone-600">{money(r.storePlanCents)}</td>
                  <td className="px-3.5 py-2 text-stone-600">{money(r.addOnCents)}</td>
                  <td className="px-3.5 py-2 text-right font-semibold">{money(r.totalCents)}</td>
                </tr>
              ))}
              <tr className="border-t border-stone-200 bg-stone-50 font-bold">
                <td className="px-3.5 py-2">12 months</td>
                <td className="px-3.5 py-2">{money(rows.reduce((n, r) => n + r.storePlanCents, 0))}</td>
                <td className="px-3.5 py-2">{money(rows.reduce((n, r) => n + r.addOnCents, 0))}</td>
                <td className="px-3.5 py-2 text-right">{money(rows.reduce((n, r) => n + r.totalCents, 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-1.5 text-[11px] text-stone-400">
          Each subscription at what Stripe bills it today. This order&apos;s hardware drops off when its term ends.
        </p>
      </div>

      {isHome && rented.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="w-full min-w-[460px] text-[12.5px] tabular-nums">
            <thead>
              <tr className="bg-stone-50 text-left text-[10.5px] uppercase tracking-wider text-stone-400">
                <th className="px-3.5 py-2 font-bold">This order&apos;s hardware</th>
                <th className="px-3.5 py-2 font-bold">Qty</th>
                <th className="px-3.5 py-2 font-bold">Term</th>
                <th className="px-3.5 py-2 text-right font-bold">Each</th>
                <th className="px-3.5 py-2 text-right font-bold">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {rented.map((i, n) => (
                <tr key={`${i.skuId}-${n}`} className="border-t border-stone-100">
                  <td className="px-3.5 py-2 text-stone-800">{nameOf(i.modelId, i.skuId)}</td>
                  <td className="px-3.5 py-2">{i.quantity || 1}</td>
                  <td className="px-3.5 py-2 text-stone-600">{i.termMonths ? `${i.termMonths} mo` : '—'}</td>
                  <td className="px-3.5 py-2 text-right">{money(i.amountCents)}</td>
                  <td className="px-3.5 py-2 text-right font-semibold">{money((i.amountCents || 0) * (i.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AccountView: React.FC<{ stores: StoreBill[]; accountCents: number; onPick: (k: string) => void }> = ({ stores, accountCents, onPick }) => (
  <div className="overflow-x-auto rounded-xl border border-stone-200">
    <table className="w-full min-w-[460px] text-[12.5px] tabular-nums">
      <thead>
        <tr className="bg-stone-50 text-left text-[10.5px] uppercase tracking-wider text-stone-400">
          <th className="px-3.5 py-2 font-bold">Store</th>
          <th className="px-3.5 py-2 font-bold">Store plan</th>
          <th className="px-3.5 py-2 font-bold">Add-ons</th>
          <th className="px-3.5 py-2 text-right font-bold">Per month</th>
        </tr>
      </thead>
      <tbody>
        {stores.map((s) => (
          <tr key={s.locationId || 'none'} className="border-t border-stone-100">
            <td className="px-3.5 py-2">
              <button type="button" onClick={() => onPick(s.locationId || '')} className="min-h-[36px] text-left font-semibold text-stone-900 hover:underline">
                {s.name}
              </button>
              {!s.locationId && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">needs a store</span>}
            </td>
            <td className="px-3.5 py-2 text-stone-600">{money(s.storePlanCents)}</td>
            <td className="px-3.5 py-2 text-stone-600">{money(s.addOnCents)}</td>
            <td className="px-3.5 py-2 text-right font-semibold">{money(s.monthlyCents)}</td>
          </tr>
        ))}
        <tr className="border-t border-stone-200 bg-stone-50 font-bold">
          <td className="px-3.5 py-2" colSpan={3}>Whole account</td>
          <td className="px-3.5 py-2 text-right">{money(accountCents)}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

export default LocationPaidPanel;
